import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, LogIn, Eye, EyeOff } from 'lucide-react';
import StyledButton from '../ui/StyledButton';

const LoginForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  
  // Use refs to avoid re-renders
  const emailRef = useRef();
  const passwordRef = useRef();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error immediately when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous errors
    setErrors({});
    
    // Basic validation
    const newErrors = {};
    
    if (!formData.email) {
      newErrors.email = 'L\'email est requis';
    }
    
    if (!formData.password) {
      newErrors.password = 'Le mot de passe est requis';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setLoading(true);

    try {
      // Direct API call instead of using context
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password
        }),
      });

      const result = await response.json();
      
      if (result.success) {
        // Store token in localStorage
        localStorage.setItem('token', result.token);
        
        // Use window.location instead of navigate to avoid component re-renders
        if (result.user.role === 'admin') {
          window.location.href = '/admin';
        } else {
          window.location.href = '/profile';
        }
        return; // Exit early to prevent further execution
      } else {
        // Handle specific login errors
        if (result.errorType === 'email_not_found') {
          setErrors({ email: 'Aucun utilisateur trouvé avec cette adresse email' });
        } else if (result.errorType === 'wrong_password') {
          setErrors({ password: 'Mot de passe incorrect' });
        } else {
          setErrors({ general: result.message || 'Erreur lors de la connexion' });
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      setErrors({ general: 'Erreur lors de la connexion' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-iranian-900 flex justify-center items-start pt-24 md:pt-28 lg:pt-32 pb-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-iranian-pattern opacity-20"></div>
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-2xl w-full space-y-12 relative z-10"
      >
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-iranian-400 to-iranian-600 rounded-2xl flex items-center justify-center shadow-iranian">
              <LogIn className="h-8 w-8 text-white" />
            </div>
          </div>
          <h2 className="text-4xl font-bold text-white mb-3">
            Se connecter
          </h2>
          <p className="text-gray-300 text-lg">
            Accédez à votre compte TOLOU
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-gray-800/90 backdrop-blur-sm rounded-2xl shadow-2xl border border-gray-700/50 p-10 space-y-6">
          {/* General Error */}
          {errors.general && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm flex items-center gap-2"
            >
              <div className="w-2 h-2 bg-red-400 rounded-full"></div>
              {errors.general}
            </motion.div>
          )}

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Email
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                ref={emailRef}
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="pl-10 w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-iranian-500 focus:border-iranian-500 transition-all duration-200"
                placeholder="votre@email.com"
              />
            </div>
            {/* Email Error */}
            {errors.email && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-2 text-red-400 text-sm flex items-center gap-2"
              >
                <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                {errors.email}
              </motion.div>
            )}
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Mot de passe
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                ref={passwordRef}
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                required
                className="pl-10 w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-iranian-500 focus:border-iranian-500 transition-all duration-200"
                placeholder="Votre mot de passe"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {/* Password Error */}
            {errors.password && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-2 text-red-400 text-sm flex items-center gap-2"
              >
                <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                {errors.password}
              </motion.div>
            )}
          </div>

          {/* Forgot Password Link */}
          <div className="text-right">
            <Link
              to="/forgot-password"
              className="text-iranian-400 hover:text-iranian-300 text-sm font-medium transition-colors duration-200 hover:underline"
            >
              Mot de passe oublié ?
            </Link>
          </div>

          {/* Submit Button */}
          <StyledButton
            type="submit"
            loading={loading}
            fullWidth
            size="lg"
            icon={LogIn}
            variant="primary"
          >
            Se connecter
          </StyledButton>

          {/* Links */}
          <div className="text-center space-y-2">
            <p className="text-gray-400">
              Pas encore de compte?{' '}
              <Link to="/register" className="text-iranian-400 hover:text-iranian-300 font-medium transition-colors duration-200">
                Créer un compte
              </Link>
            </p>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default LoginForm;
